/**
 * Which signaling backend to use.
 *
 * Two tabs on one machine need no server at all, so local development defaults
 * to BroadcastChannel. Anywhere else the PHP endpoints are assumed to be live
 * at /api. `?signal=http|local` forces either, and `?api=<base>` points at a
 * different mount point.
 */
import { BroadcastSignaling, HttpSignaling, type Signaling, makeRoomCode } from '@office/net';

export type Backend = 'local' | 'http';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '']);

export function apiBase(): string {
  return new URLSearchParams(location.search).get('api') ?? '/api';
}

export function chooseBackend(): Backend {
  const forced = new URLSearchParams(location.search).get('signal');
  if (forced === 'http' || forced === 'local') return forced;
  return LOCAL_HOSTS.has(location.hostname) ? 'local' : 'http';
}

export function makeSignaling(room: string, backend: Backend = chooseBackend()): Signaling {
  return backend === 'local'
    ? new BroadcastSignaling(room)
    : new HttpSignaling(room, { base: apiBase() });
}

/**
 * Get a room code. Over HTTP the server allocates it, so two people hosting at
 * once cannot collide; locally there is nobody to collide with.
 */
export async function allocateRoom(backend: Backend = chooseBackend()): Promise<string> {
  if (backend === 'local') return makeRoomCode();
  return HttpSignaling.createRoom(apiBase());
}
